import React, { useState, useMemo, useEffect } from "react";
import { Icon, Table, Button } from "@citi-icg-172888/icgds-react";
import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";
import Papa from "papaparse";
import { POST_LEAD_LIST_BY_LEADID } from "../../services/api/apiUrls";
import { updateDataByLeadId } from "../../services/api/apiService";
import SelectHoc from "../../common/SelectHoc";

interface Column {
  field: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  expandable?: boolean;
  type?: "text" | "number" | "date";
}

interface Row {
  [key: string]: any;
}

interface Props {
  columns: Column[];
  data: Row[];
  pageSize?: number;
}
const columnData = [
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
  },
  {
    title: "Criteria",
    dataIndex: "criteria",
    key: "criteria",
  },
  {
    title: "Score",
    dataIndex: "score",
    key: "score",
  }
];

const FONT_OPTIONS = [
  "Arial, sans-serif",
  "'Courier New', monospace",
  "'Georgia', serif",
  "'Tahoma', sans-serif",
  "'Verdana', sans-serif",
];
const getFontColor = (bgColor: string): string => {
  // Convert HEX to RGB
  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance (formula used in W3C accessibility guidelines)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "#000000" : "#ffffff"; // Dark text on light bg, Light text on dark bg
};

const CreateTable: React.FC<Props> = ({ columns, data, pageSize = 10 }) => {
  const [tableStyle, setTableStyle] = useState("lmn-table-bordered");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [backgroundColor, setBackgroundColor] = useState(
    () => localStorage.getItem("tableBgColor") || "#ffffff"
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fontFamily, setFontFamily] = useState(
    () => localStorage.getItem("tableFont") || FONT_OPTIONS[0]
  );
  const [fontColor, setFontColor] = useState(getFontColor(backgroundColor));

  // Store user settings in localStorage when changed
  useEffect(() => {
    localStorage.setItem("tableBgColor", backgroundColor);
    setFontColor(getFontColor(backgroundColor)); // Update font color dynamically
  }, [backgroundColor]);

  useEffect(() => {
    localStorage.setItem("tableFont", fontFamily);
  }, [fontFamily]);

  const handleDownloadCSV = (item: any) => {
    // Flatten the data structure for CSV export
    const csvData: any = [];
    if (item === "paginated") {
      paginatedData.forEach((data) => {
        // Add parent row
        csvData.push({
          "Lead Id": data.leadId,
          "Business Name": data.businessName,
          Status: data.status,
          Date: data.updtEpoch,
          Category: "",
          Criteria: "",
          Score: "",
			Consent:"Yes",
        });

        // Add child rows
        data.leadAndScoreDetails.forEach((detail: any) => {
          csvData.push({
            "Lead Id": "",
            "Business Name": "",
            Status: "",
            Date: "",
            Category: detail.category,
            Criteria: detail.criteria,
            Score: detail.score,	
			
          });
        });
      });
    } else {
      sortedData.forEach((data) => {
        // Add parent row
        csvData.push({
          "Lead Id": data.leadId,
          "Business Name": data.businessName,
          Status: data.status,
          Date: data.updtEpoch,
          Category: "",
          Criteria: "",
          Score: "",	
			Consent:"Yes",
        });

        // Add child rows
        data.leadAndScoreDetails.forEach((detail: any) => {
          csvData.push({
            "Lead Id": "",
            "Business Name": "",
            Status: "",
            Date: "",
            Category: detail.category,
            Criteria: detail.criteria,
            Score: detail.score,												
			
          });
        });
      });
    }

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const retrySaveLead = async (leadId: any) => {
    const endpoint = POST_LEAD_LIST_BY_LEADID + leadId;
    try {
      const response = await updateDataByLeadId(endpoint);
      if (response.status === 200) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error retrying save:", error);
    }
  };

  // Toggle Expand/Collapse
  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const searchInObject = (obj: any, searchText: any): any => {
    if (
      Object.prototype.hasOwnProperty.call(obj, "businessName") &&
      obj.businessName !== null &&
      obj.businessName.toString().toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    }

    if (
      Object.prototype.hasOwnProperty.call(obj, "leadId") &&
      obj.leadId !== null &&
      obj.leadId.toString().toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      let matchesSearch = false;
      let matchesDate = false;
      let matchesStatus = false;

      // 🔹 Search Text Filtering (Fix: Ensures it applies correctly)
      if (searchText.trim() !== "") {
        matchesSearch = searchInObject(row, searchText);
      } else {
        matchesSearch = true; // No search input means include all
      }

      // 🔹 Date Filtering (Fix: Ensures exact match)
      if (searchDate) {
        const rowDate = new Date(row.isrtEpoch).toISOString().split("T")[0]; // Normalize date
        const formattedSearchDate = new Date(searchDate).toISOString().split("T")[0];
        matchesDate = rowDate === formattedSearchDate;
      } else {
        matchesDate = true; // No date input means include all
      }

      if (leadStatus) {
        const rowStatus = row.status;
        matchesStatus = rowStatus === leadStatus;
      } else {
        matchesStatus = true; // No date input means include all
      }

      // 🔹 Return only rows that match three filters
      return matchesSearch && matchesDate && matchesStatus;
    });
  }, [searchText, searchDate, leadStatus, data]);

  const formatDate = (timestamp: number) => {
    const parsedDate = parseTimestamp(timestamp);
    return format(parsedDate, "yyyy-MM-dd");
  };

  const parseTimestamp = (timestamp: number) => {
    // Convert Unix timestamp to ISO string
    const isoString = new Date(timestamp).toISOString();
    // Parse the ISO string
    return parseISO(isoString);
  };

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return filteredData;
    return [...filteredData].sort((a, b) => {
      const column = columns.find((col: any) => col.field === sortField);
      if (!column) return 0;

      let aValue = a[sortField];
      let bValue = b[sortField];

      if (column.type === "date") {
        aValue = parseTimestamp(aValue).getTime();
        bValue = parseTimestamp(bValue).getTime();
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [sortField, sortOrder, filteredData, columns]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div style={{ padding: "20px", maxWidth: "100%", background: backgroundColor, fontFamily }}>
      <div className="lmn-my-5 lmn-dcna-xl-flex lmn-dcna-l-flex lmn-d-md-flex">
        <div className="lmn-form-group lmn-mb-5 lmn-mr-2xl-5 lmn-mr-xl-5 lmn-dnca-w-m-half lmn-dnca-w-l-half lmn-dnca-w-xl-half">
          <div className="busniess-needs-label lmn-mb-3">Business Name or Lead ID</div>
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: "10px",
              width: "220px",
              borderRadius: "8px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <div className="lmn-form-group lmn-flex-grow-1 lmn-ml-md-5 lmn-ml-lg-8 lmn-ml-xl-8 lmn-dnca-w-m-half lmn-dnca-w-l-half lmn-dnca-w-xl-half lmn-ml-2xl-8">
          <div className=" busniess-needs-label lmn-mb-3">Created Date</div>
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <div className="lmn-form-group lmn-flex-grow-1 lmn-ml-md-5 lmn-ml-lg-8 lmn-ml-xl-8 lmn-dnca-w-m-half lmn-dnca-w-l-half lmn-dnca-w-xl-half lmn-ml-2xl-8">
          <div className="busniess-needs-label lmn-mb-3">Status</div>
          <SelectHoc
            id="lead-status"
            label="status"
            value={leadStatus}
            placeholder="Select Lead Status"
            options={["Success", "Fail"]}
            onChange={(value: any) => setLeadStatus(value)}
            allowClear={true}
          />
        </div>
      </div>
      {/* Search Inputs & Format Dropdown */}
      {/*
        <div style={{ display: "flex", alignItems: "center", width: "20%", gap: "20px" }}>
          <Input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} />
          <Select value={fontFamily} onChange={(e) => setFontFamily(e)}>
            {FONT_OPTIONS.map((font, index) => (
              <Option key={index} value={font}>
                {font}
              </Option>
            ))}
          </Select>
        </div>
      </div>
		*/}

      {/* Table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          borderRadius: "10px",
          overflow: "hidden",
          background: backgroundColor,
          fontFamily,
          color: fontColor,
        }}
      >
        <thead>
          <tr style={{ background: backgroundColor, fontFamily, color: fontColor }}>
            {columns.map((col) => (
              <th
                key={col.field}
                style={{
                  padding: "12px",
                  borderBottom: "2px solid #ddd",
                  cursor: col.sortable ? "pointer" : "default",
                  textAlign: "left",
                }}
                onClick={() => col.sortable && handleSort(col.field)}
              >
                {col.header}
                {col.sortable && (
                  <>
                    {" "}
                    {sortField === col.field ? (
                      sortOrder === "asc" ? (
                        <Icon type="caret-up" />
                      ) : (
                        <Icon type="caret-down" />
                      )
                    ) : (
                      <Icon type="vertical" />
                    )}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ background: backgroundColor }}>
          {paginatedData.map((row) => (
            <React.Fragment key={row.leadId}>
              <tr
                style={{
                  borderBottom: "1px solid #ddd",
                  background: expandedRows[row.leadId] ? backgroundColor : backgroundColor,
                  fontFamily,
                  color: fontColor,
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.field}
                    style={{ padding: "12px", textAlign: "left", backgroundColor }}
                  >
                    {col.expandable ? (
                      <button
                        onClick={() => {
                          setTableStyle("lmn-table-bordered");
                          toggleExpand(row.leadId);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#007bff",
                          fontSize: "16px",
                        }}
                      >
                        {expandedRows[row.leadId] ? (
                          <Icon
                            type="minus-circle"
                            style={{ color: getFontColor(backgroundColor) }}
                          />
                        ) : (
                          <Icon
                            type="add-circle"
                            style={{ color: getFontColor(backgroundColor) }}
                          />
                        )}
                      </button>
                    ) : col.type === "date" ? (
                      formatDate(row[col.field])
                    ) : col.field == "showButton" && row["status"] === "Fail" ? (
                      <button
                        onClick={() => retrySaveLead(row.leadId)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          background: "#007bff",
                          color: "white",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Retry Save
                      </button>
                    ) : col.field == "showButton" && row["status"] !== "Fail" ? (
                      <></>
                    ) : (
                      String(row[col.field])
                    )}
                  </td>
                ))}
              </tr>

              {/* Expanded View */}
              {expandedRows[row.leadId] && row.leadAndScoreDetails && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ padding: "12px", background: "#f8f9fa", backgroundColor }}
                  >
                    <Table
                      className={tableStyle}
                      size={"default"}
                      data={row.leadAndScoreDetails}
                      columns={columnData}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div
        style={{
          marginTop: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: backgroundColor,
          fontFamily,
          color: fontColor,
        }}
      >
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            background: "#007bff",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Prev
        </button>
        <Button onClick={() => handleDownloadCSV("sort")} className="download-csv-button">
          Download Sorted Data CSV
        </Button>
        <span>
          {" "}
          Page {currentPage} of {totalPages}{" "}
        </span>
        <Button onClick={() => handleDownloadCSV("paginated")} className="download-csv-button">
          Download Current Data CSV
        </Button>

        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            background: "#007bff",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CreateTable;
